import { Box, Typography } from "@mui/material";
import {
  type ChessArrow as Arrow,
  type ChessSquareStyles as CustomSquareStyles,
  type ChessSquare as Square,
  useEvaluationGutterWidth,
} from "@/shared/components/chessboard";
import { useEffect, useState, type ReactNode, type RefObject } from "react";
import {
  CapturedPieces,
  ReviewBoardSettingsDialog,
  type ReviewBoardSettings,
  type ReviewBoardSettingsPatch,
} from "@/features/board";
import type { Evaluation } from "@/shared/components/evaluation";
import type { AnalyzedMoveResult } from "@/shared/api/engine.api";
import { BoardEvidenceBadges } from "./BoardEvidenceBadges";
import type { BoardEvidenceFocus } from "@/shared/components/chessboard/BoardEvidenceWheel";
import { principleMarkers, visiblePrincipleSignals } from "./principle-markers";
import type { TemporaryPlayController } from "@/features/board/temporary-play";
import type { WinningOutcomeMotif } from "../game-outcome";
import type { TemporaryMoveEvidence } from "./temporary-move-evidence";
import { ChesslaBoardSurface, BoardFrame } from "@/features/board/surface";

interface BoardDisplayProps {
  relationshipFocus?: BoardEvidenceFocus | null;
  relationshipArrows?: boolean;
  fen: string;
  orientation: "white" | "black";
  boardWidth: number;
  arrows: Arrow[];
  squareStyles: CustomSquareStyles;
  evidenceArrows?: Arrow[];
  evidenceSquareStyles?: CustomSquareStyles;
  evidenceDescription?: string;
  boardId: string;
  /** Mutable ref for the board wrapper element — typed as React allows for useRef<T | null>. */
  boardWrapperRef: RefObject<HTMLDivElement | null>;
  evaluation: Evaluation | null;
  evaluationPending?: boolean;
  currentMove: AnalyzedMoveResult | null;
  bookReference?: string | null;
  outcomeMotif?: WinningOutcomeMotif | null;
  includeOpponentEvidence?: boolean;
  decisionLabel?: string | null;
  notice?: ReactNode;
  playerRows?: { top: ReactNode; bottom: ReactNode };
  temporaryPlay: TemporaryPlayController | null;
  promotionDialog?: ReactNode;
  temporarySquareStyles: CustomSquareStyles;
  temporaryEvidence?: TemporaryMoveEvidence | null;
  onPlayVerifiedLine?: () => void;
  onActiveChange?: (focus: BoardEvidenceFocus | null) => void;
  onPieceDrop?: (source: string, target: string) => boolean;
  onSquareClick?: (square: Square) => void;
  revealEvidence?: boolean;
  showMotifs?: boolean;
  settings: ReviewBoardSettings;
  settingsOpen: boolean;
  onSettingsClose: () => void;
  onSettingsChange: (settings: ReviewBoardSettingsPatch) => void;
}

/**
 * The chessboard, eval bar, and captured pieces display for the game review board.
 * Purely presentational — all state lives in GameBoard (orchestrator).
 */
export const BoardDisplay = ({
  relationshipFocus,
  relationshipArrows = true,
  fen,
  orientation,
  boardWidth,
  arrows,
  squareStyles,
  evidenceArrows = [],
  evidenceSquareStyles = {},
  evidenceDescription = "",
  boardId,
  boardWrapperRef,
  evaluation,
  evaluationPending = false,
  currentMove,
  bookReference,
  outcomeMotif = null,
  includeOpponentEvidence = false,
  decisionLabel = null,
  notice,
  playerRows,
  temporaryPlay,
  promotionDialog,
  temporarySquareStyles,
  temporaryEvidence = null,
  onPlayVerifiedLine,
  onActiveChange,
  onPieceDrop,
  onSquareClick,
  revealEvidence = true,
  showMotifs = true,
  settings,
  settingsOpen,
  onSettingsClose,
  onSettingsChange,
}: BoardDisplayProps) => {
  const gutter = useEvaluationGutterWidth();
  const [visibleEvidenceMove, setVisibleEvidenceMove] = useState<AnalyzedMoveResult | null>(null);

  // react-chessboard applies externally supplied positions on a zero-delay
  // timer. Match that lifecycle so the badge and piece land together.
  useEffect(() => {
    setVisibleEvidenceMove(null);
    const timer = window.setTimeout(() => setVisibleEvidenceMove(currentMove), 0);
    return () => window.clearTimeout(timer);
  }, [fen, currentMove]);
  const temporaryActive = Boolean(temporaryPlay && temporaryPlay.state.moves.length > 0);
  const principlePresentation = principleMarkers(
    temporaryEvidence?.principleSignals ??
      visiblePrincipleSignals(visibleEvidenceMove, fen, revealEvidence, temporaryActive),
  );

  return (
    <Box sx={{ py: { xs: 1, lg: 0 }, flexShrink: 0 }}>
      <Box ref={boardWrapperRef} sx={{ width: "100%" }}>
        <Box sx={{ width: boardWidth + gutter, maxWidth: "100%", mx: "auto" }}>
          {playerRows?.top}
          <BoardFrame
            width={boardWidth}
            evaluation={
              revealEvidence && settings.showEvaluation
                ? { evaluation, orientation, pending: evaluationPending, fen }
                : undefined
            }
          >
            <Box data-review-square sx={{ position: "relative", width: "100%" }}>
              <ChesslaBoardSurface
                settings={settings}
                relationships={revealEvidence}
                relationshipArrows={relationshipArrows}
                relationshipFocus={relationshipFocus}
                key={boardId}
                id={boardId}
                position={fen}
                orientation={orientation}
                width="100%"
                animationDuration={0}
                allowDragging={onPieceDrop != null}
                onPieceDrop={onPieceDrop}
                onSquareClick={onSquareClick}
                arrows={[
                  ...arrows,
                  ...evidenceArrows,
                  ...(settings.showPrincipleSignals && settings.showBestMoveArrow ? principlePresentation.arrows : []),
                ].filter(
                  (arrow, index, all) =>
                    all.findIndex(
                      (candidate) =>
                        candidate.startSquare === arrow.startSquare &&
                        candidate.endSquare === arrow.endSquare &&
                        candidate.color === arrow.color,
                    ) === index,
                )}
                squareStyles={{
                  ...squareStyles,
                  ...evidenceSquareStyles,
                  ...(settings.showPrincipleSignals && settings.showPositionMarkers
                    ? principlePresentation.squareStyles
                    : {}),
                  ...temporarySquareStyles,
                }}
                evidence={(relationships) => (
                  <BoardEvidenceBadges
                    relationships={relationships}
                    bookReference={bookReference}
                    move={visibleEvidenceMove}
                    displayFen={fen}
                    orientation={orientation}
                    showMotifs={showMotifs}
                    showTactics={settings.showTacticalMotifs}
                    showPrinciples={settings.showPrincipleSignals}
                    includeOpponentEvidence={includeOpponentEvidence}
                    outcomeMotif={outcomeMotif}
                    reviewDecision={!!decisionLabel}
                    revealEvidence={revealEvidence}
                    temporaryActive={temporaryActive}
                    temporaryEvidence={temporaryEvidence}
                    onPlayVerifiedLine={onPlayVerifiedLine}
                    onActiveChange={onActiveChange}
                  />
                )}
              />
              {(evidenceDescription || principlePresentation.description) && (
                <Typography
                  sx={{
                    position: "absolute",
                    width: "1px",
                    height: "1px",
                    p: 0,
                    m: -1,
                    overflow: "hidden",
                    clip: "rect(0 0 0 0)",
                    whiteSpace: "nowrap",
                    border: 0,
                  }}
                >
                  {[evidenceDescription, principlePresentation.description].filter(Boolean).join(". ")}
                </Typography>
              )}
              {notice}
              {promotionDialog && (
                <Box
                  sx={{
                    position: "absolute",
                    insetInline: 0,
                    top: 0,
                    zIndex: (theme) => theme.zIndex.modal,
                    bgcolor: "background.paper",
                  }}
                >
                  {promotionDialog}
                </Box>
              )}
            </Box>
          </BoardFrame>
          {playerRows?.bottom}
        </Box>
        {!playerRows && <CapturedPieces fen={fen} />}
        <ReviewBoardSettingsDialog
          open={settingsOpen}
          settings={settings}
          onChange={onSettingsChange}
          onClose={onSettingsClose}
        />
      </Box>
    </Box>
  );
};
